import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, ArrowUpRightIcon } from "lucide-react";
import { projects } from "../data/projects";
import { caseStudies } from "../data/caseStudies";
import { DISPLAY_HEADING, EYEBROW, H2, LEAD } from "../utils/typography";
import { TreatedImage } from "../components/TreatedImage";
import { ImagePlaceholder } from "../components/caseStudy/ImagePlaceholder";
import { ChallengeSolution } from "../components/caseStudy/ChallengeSolution";
import { ResultsSection } from "../components/caseStudy/ResultsSection";
import { ProcessSection } from "../components/caseStudy/ProcessSection";
import { FeaturesSection } from "../components/caseStudy/FeaturesSection";
import { KeyDecisions } from "../components/caseStudy/KeyDecisions";
import { BeforeAfter } from "../components/caseStudy/BeforeAfter";
import { ReflectionSection } from "../components/caseStudy/ReflectionSection";
import { GoDeeper } from "../components/caseStudy/GoDeeper";
import { RelatedThoughts } from "../components/caseStudy/RelatedThoughts";
import { Reveal } from "../components/Reveal";
import { usePageTitle } from "../hooks/usePageTitle";
export function CaseStudy() {
  const {
    id
  } = useParams();
  const caseStudyProjects = projects.filter(p => p.kind === "case-study");
  const project = projects.find(p => p.id === id && (p.kind === "case-study" || p.kind === "in-progress"));
  const index = caseStudyProjects.findIndex(p => p.id === id);
  const content = project ? caseStudies[project.id] : undefined;
  const inProgress = project?.kind === "in-progress";
  const back = inProgress ? {
    to: "/#now",
    label: "In progress"
  } : {
    to: "/#projects",
    label: "All projects"
  };
  usePageTitle(project && content ? project.title : "Project not found");
  if (!project || !content) {
    return <main id="main-content" tabIndex={-1} className="flex min-h-screen w-full flex-col items-start justify-center bg-cream px-6 md:px-12 lg:px-20" data-id="element-505" data-mp="hiopecg7p5wu">
        <p className="text-sm font-medium uppercase tracking-wide text-rust" data-id="element-506" data-mp="36aa7pewcsghg">Not found</p>
        <h1 className={`mt-4 ${DISPLAY_HEADING} text-espresso`} data-id="element-507" data-mp="3b7sgn5adevpg">
          This project isn’t here.
        </h1>
        <Link to="/#projects" className="mt-10 inline-flex items-center gap-2 rounded-full bg-espresso px-6 py-3 text-sm font-medium uppercase text-cream transition-colors duration-150 hover:bg-rust" data-id="element-508" data-mp="3fb8cw0ij2u1m">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-509" data-mp="2nku2ie9q3zup" />
          All projects
        </Link>
      </main>;
  }

  // Follow homepage order, looping from the last case study back to the first.
  // In-progress work isn't in the sequence, so it leads into the first case study.
  const next = caseStudyProjects[inProgress ? 0 : (index + 1) % caseStudyProjects.length];
  const nextLabel = `Next project / ${caseStudies[next.id]?.number ?? ""}`;
  return <main id="main-content" tabIndex={-1} className="w-full bg-cream pb-24 pt-36 md:pb-32 md:pt-48 lg:pb-40" data-id="element-510" data-mp="28tl3xfnuf45f">
      <article data-id="element-511" data-mp="21atyl588qnyu">
        {/* Title */}
        <Reveal className="px-6 md:px-12 lg:px-20" data-id="element-512" data-mp="219smqyjehxg2">
        <header data-id="element-513" data-mp="1wb7oh4nplnu9">
          <Link to={back.to} className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-espresso/70 transition-colors duration-150 hover:text-rust" data-id="element-514" data-mp="1wcip4vluyfh5">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-515" data-mp="1na9g6a68mu5v" />
            {back.label}
          </Link>
          <p className="mt-10 text-sm font-medium uppercase tracking-wide text-rust" data-id="element-516" data-mp="1icho9g5lf81o">
            {content.number ? `Case study / ${content.number} · ${project.client}` : `Case study / ${project.client}`}
          </p>
          <h1 className={`mt-4 max-w-[16ch] ${DISPLAY_HEADING} text-espresso`} data-id="element-517" data-mp="13mtrms8cwxka">
            {project.title}
          </h1>
        </header>
        </Reveal>

        {/* Hero image */}
        <Reveal className="mt-12 px-6 md:mt-16 md:px-12 lg:px-20" delay={0.06} data-id="element-518" data-mp="zn1e0143w182">
          {project.image ? <TreatedImage src={project.image} alt={`${project.title} — ${project.client}`} className="aspect-[16/9] w-full rounded-3xl bg-paper" motionMask={project.motionMask} motionStyle={project.motionStyle} position={project.heroPosition} eager data-id="element-519" data-mp="2uod67d9y1bq1" /> : <ImagePlaceholder caption="Hero image" className="aspect-[16/9] w-full" data-id="element-520" data-mp="2pvk30tu4ibg2" />}
        </Reveal>

        <div className="px-6 md:px-12 lg:px-20" data-id="element-521" data-mp="2b2qk0yfn7d6n">
          {/* Lede + facts */}
          <Reveal className="mt-16 grid grid-cols-1 gap-12 md:mt-24 lg:grid-cols-12" data-id="element-522" data-mp="2fxe1weg559c9">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-6 self-start lg:col-span-4 lg:grid-cols-1" data-id="element-523" data-mp="221aep53oq19a">
              {content.facts.map(fact => <div key={fact.label} data-id="element-524" data-mp="8m7kknx6to8">
                  <dt className={`${EYEBROW} text-espresso/70`} data-id="element-525" data-mp="8vwe4x8ausc">
                    {fact.label}
                  </dt>
                  <dd className="mt-1 text-espresso" data-id="element-526" data-mp="55cvfu5yutk7">{fact.value}</dd>
                </div>)}
            </dl>
            <p className={`${LEAD} text-espresso lg:col-span-8`} data-id="element-527" data-mp="ac9nox0uylje">
              {content.lede}
            </p>
          </Reveal>

          <div className="mt-24 space-y-24 md:mt-32 md:space-y-32 lg:mt-40 lg:space-y-40" data-id="element-528" data-mp="o3xucksc8hsz">
            <Reveal data-id="element-529" data-mp="jcpmht6np4hx">
              <ChallengeSolution challenge={content.challenge} solution={content.solution} data-id="element-530" data-mp="jc63sa96mc2u" />
            </Reveal>
            <Reveal data-id="element-531" data-mp="y0aotkvpizos">
              <ResultsSection results={content.results} data-id="element-532" data-mp="y3oryvc73cxa" />
            </Reveal>
            {content.features && <Reveal data-id="element-533" data-mp="172jvu0rroaxf">
                <FeaturesSection features={content.features} data-id="element-534" data-mp="172tq40juh48x" />
              </Reveal>}
            <Reveal data-id="element-535" data-mp="130oyf7cyihox">
              <ProcessSection process={content.process} data-id="element-536" data-mp="1329tqz73i4nu" />
            </Reveal>
            <Reveal data-id="element-537" data-mp="38t55jg41un3q">
              <KeyDecisions decisions={content.decisions} data-id="element-538" data-mp="38uonrc99n885" />
            </Reveal>
            {content.beforeAfter && <Reveal data-id="element-539" data-mp="34u4oo354tujd">
                <BeforeAfter panels={content.beforeAfter} data-id="element-540" data-mp="17rp51rygy0cj" />
              </Reveal>}
            <Reveal data-id="element-541" data-mp="17u1wqwu13p2s">
              <ReflectionSection learned={content.learned} data-id="element-542" data-mp="1cq8s5vosti6h" />
            </Reveal>
            {content.relatedThoughts.length > 0 && <Reveal data-id="element-543" data-mp="1hp295y3anrbe">
                <RelatedThoughts slugs={content.relatedThoughts} data-id="element-544" data-mp="1hry4rfro63wo" />
              </Reveal>}
            {content.deeper && <Reveal data-id="element-545" data-mp="1qr33am38dx8u">
                <GoDeeper deeper={content.deeper} data-id="element-546" data-mp="1qpiab0wkemd3" />
              </Reveal>}
          </div>
        </div>
      </article>

      {/* Next project */}
      {next && <Reveal data-id="element-547" data-mp="20ftdgl8fcrix">
        <nav aria-label="Next project" className="mx-6 mt-24 md:mx-12 md:mt-32 lg:mx-20 lg:mt-40" data-id="element-548" data-mp="20he788mx06fq">
          <Link to={next.href} className="group flex items-end justify-between gap-6" data-id="element-549" data-mp="25eedajjrl96u">
            <div data-id="element-550" data-mp="2afbuast1fx6r">
              <p className="text-sm font-medium uppercase tracking-wide text-espresso/70" data-id="element-551" data-mp="2aftziexohlqt">
                {nextLabel}
              </p>
              <p className={`mt-3 ${H2} text-espresso transition-colors duration-150 group-hover:text-rust`} data-id="element-552" data-mp="2ee1dj20rc8yb">
                {next.title}
              </p>
            </div>
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-espresso text-cream transition-colors duration-150 group-hover:bg-rust" data-id="element-553" data-mp="3uv072p3dpdrt">
              <ArrowUpRightIcon className="h-5 w-5" data-id="element-554" data-mp="3kz6y1fovc2es" />
            </span>
          </Link>
        </nav>
        </Reveal>}
    </main>;
}